import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Spinner } from '../components/Spinner';
import { useAuth } from '../contexts/AuthContext';
import {
  approveSupportAccessRequest,
  denySupportAccessRequest,
  revokeSupportAccessRequest,
} from '../lib/supportAccessClient';
import { supabase } from '../lib/supabase';
import type { SupportAccessRequestRow, SupportAccessRequestStatus } from '../types/supportAccess';
import { cardClass } from './settings/styles';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function statusBadgeClasses(status: SupportAccessRequestStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-warning/15 text-warning ring-1 ring-warning/35';
    case 'active':
      return 'bg-danger/15 text-danger ring-1 ring-danger/35';
    case 'revoked':
    case 'expired':
    default:
      return 'bg-foreground/10 text-muted ring-1 ring-border';
  }
}

function statusLabel(status: SupportAccessRequestStatus): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'active':
      return 'Active';
    case 'revoked':
      return 'Revoked';
    case 'expired':
      return 'Expired';
    default:
      return status;
  }
}

type TokenFetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'not_found' }
  | { kind: 'found'; row: SupportAccessRequestRow };

type HistoryState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty' }
  | { kind: 'ready'; rows: SupportAccessRequestRow[] };

export default function SupportAccessPage() {
  const { currentUser, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => {
    const t = searchParams.get('token');
    return t && t.trim().length > 0 ? t.trim() : null;
  }, [searchParams]);

  const [tokenState, setTokenState] = useState<TokenFetchState>({ kind: 'idle' });
  const [historyState, setHistoryState] = useState<HistoryState>({ kind: 'idle' });
  const [tokenActionBusy, setTokenActionBusy] = useState(false);
  const [tokenOutcome, setTokenOutcome] = useState<
    | null
    | { type: 'approved'; expiresAt: string }
    | { type: 'denied' }
  >(null);
  const [revokeModalRequestId, setRevokeModalRequestId] = useState<string | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [historyRevokeSuccess, setHistoryRevokeSuccess] = useState(false);
  const [tokenActionError, setTokenActionError] = useState<string | null>(null);

  const userId = currentUser?.id ?? null;

  useEffect(() => {
    setTokenOutcome(null);
    setTokenActionError(null);
  }, [token]);

  const loadHistory = useCallback(async () => {
    if (!userId) return;
    setHistoryState({ kind: 'loading' });
    const { data, error } = await supabase
      .from('support_access_requests')
      .select(
        'id, user_id, session_token, reason, status, requested_at, expires_at, approved_at, revoked_at, revoked_by'
      )
      .eq('user_id', userId)
      .order('requested_at', { ascending: false });

    if (error) {
      setHistoryState({ kind: 'error', message: error.message });
      return;
    }
    const rows = (data ?? []) as SupportAccessRequestRow[];
    if (rows.length === 0) {
      setHistoryState({ kind: 'empty' });
      return;
    }
    setHistoryState({ kind: 'ready', rows });
  }, [userId]);

  useEffect(() => {
    if (authLoading || !userId) return;

    if (!token) {
      setTokenState({ kind: 'idle' });
      void loadHistory();
      return;
    }

    let cancelled = false;
    setTokenState({ kind: 'loading' });

    void (async () => {
      const { data, error } = await supabase
        .from('support_access_requests')
        .select(
          'id, user_id, session_token, reason, status, requested_at, expires_at, approved_at, revoked_at, revoked_by'
        )
        .eq('session_token', token)
        .in('status', ['pending', 'active'])
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        setTokenState({ kind: 'error', message: error.message });
        void loadHistory();
        return;
      }
      if (!data) {
        setTokenState({ kind: 'not_found' });
        void loadHistory();
        return;
      }
      setTokenState({ kind: 'found', row: data as SupportAccessRequestRow });
      void loadHistory();
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, userId, token, loadHistory]);

  const activeRequestFromHistory = useMemo(() => {
    if (historyState.kind !== 'ready') return null;
    return historyState.rows.find((r) => r.status === 'active') ?? null;
  }, [historyState]);

  const handleApprove = async (row: SupportAccessRequestRow) => {
    if (!userId) return;
    setTokenActionBusy(true);
    setTokenActionError(null);
    const { errorMessage } = await approveSupportAccessRequest({ requestId: row.id, userId });
    setTokenActionBusy(false);
    if (errorMessage) {
      setTokenActionError(errorMessage);
      setTokenOutcome(null);
      return;
    }
    setTokenOutcome({ type: 'approved', expiresAt: row.expires_at });
    setTokenState({ kind: 'found', row: { ...row, status: 'active', approved_at: new Date().toISOString() } });
    void loadHistory();
  };

  const handleDeny = async (row: SupportAccessRequestRow) => {
    if (!userId) return;
    setTokenActionBusy(true);
    setTokenActionError(null);
    const { errorMessage } = await denySupportAccessRequest({ requestId: row.id, userId });
    setTokenActionBusy(false);
    if (errorMessage) {
      setTokenActionError(errorMessage);
      return;
    }
    setTokenOutcome({ type: 'denied' });
    void loadHistory();
  };

  const confirmRevoke = async (requestId: string) => {
    if (!userId) return;
    setRevokeBusy(true);
    setHistoryRevokeSuccess(false);
    const { errorMessage } = await revokeSupportAccessRequest({ requestId, userId });
    setRevokeBusy(false);
    setRevokeModalRequestId(null);
    if (errorMessage) return;
    setHistoryRevokeSuccess(true);
    window.setTimeout(() => setHistoryRevokeSuccess(false), 5000);
    if (tokenState.kind === 'found' && tokenState.row.id === requestId) {
      setTokenState({
        kind: 'found',
        row: {
          ...tokenState.row,
          status: 'revoked',
          revoked_at: new Date().toISOString(),
          revoked_by: 'customer',
        },
      });
    }
    void loadHistory();
  };

  if (authLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" aria-busy="true">
        <Spinner className="h-8 w-8 text-accent" />
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const showTokenRequestUi =
    tokenState.kind === 'found' &&
    tokenOutcome === null &&
    (tokenState.row.status === 'pending' || tokenState.row.status === 'active');

  return (
    <div className="mx-auto max-w-3xl pb-10">
      <div className="mb-6 lg:mb-8">
        <p className="text-sm text-muted">Settings</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          Support access
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted">
          Approve, monitor, or revoke temporary logNsec administrator access to your ShieldSME account.
        </p>
      </div>

      {token && tokenState.kind === 'loading' ? (
        <div className="mb-8 flex items-center gap-3 rounded-xl border border-border bg-surface/80 px-4 py-4">
          <Spinner className="h-6 w-6 shrink-0 text-accent" />
          <p className="text-sm text-muted">Loading access request…</p>
        </div>
      ) : null}

      {token && tokenState.kind === 'error' ? (
        <div
          className="mb-8 rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger"
          role="alert"
        >
          {tokenState.message}
        </div>
      ) : null}

      {token && tokenState.kind === 'not_found' ? (
        <div className="mb-6 rounded-xl border border-border bg-surface/80 px-4 py-3 text-sm text-muted">
          We could not find an open request for this link. It may have expired or already been actioned. Review your
          history below.
        </div>
      ) : null}

      {showTokenRequestUi && tokenState.kind === 'found' ? (
        <>
          <div className="mb-4 rounded-xl border border-warning/50 bg-warning/10 px-4 py-3 text-sm text-foreground">
            <p className="font-semibold text-warning">A logNsec administrator has requested temporary access to your account</p>
          </div>

          <div className={cardClass}>
            <h2 className="text-lg font-semibold text-foreground">Access request</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="text-muted">Reason for access</dt>
                <dd className="mt-1 font-medium text-foreground">
                  {tokenState.row.reason.trim() || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Requested at</dt>
                <dd className="mt-1 text-foreground">{formatDateTime(tokenState.row.requested_at)}</dd>
              </div>
              <div>
                <dt className="text-muted">Expires at</dt>
                <dd className="mt-1 text-foreground">{formatDateTime(tokenState.row.expires_at)}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm text-muted">
              This session will give a logNsec support agent read access to your dashboard to help resolve your
              issue.
            </p>

            {tokenActionError ? (
              <p className="mt-4 text-sm text-danger" role="alert">
                {tokenActionError}
              </p>
            ) : null}

            {tokenState.row.status === 'pending' ? (
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  disabled={tokenActionBusy}
                  onClick={() => void handleApprove(tokenState.row)}
                  className="rounded-lg border border-accent/50 bg-accent px-4 py-2.5 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:opacity-60"
                >
                  {tokenActionBusy ? 'Working…' : 'Approve Access'}
                </button>
                <button
                  type="button"
                  disabled={tokenActionBusy}
                  onClick={() => void handleDeny(tokenState.row)}
                  className="rounded-lg border-2 border-danger px-4 py-2.5 text-sm font-semibold text-danger transition hover:bg-danger/10 disabled:opacity-60"
                >
                  Deny Access
                </button>
              </div>
            ) : (
              <div className="mt-6">
                <button
                  type="button"
                  disabled={tokenActionBusy || revokeBusy}
                  onClick={() => setRevokeModalRequestId(tokenState.row.id)}
                  className="w-full rounded-lg border-2 border-danger bg-danger/10 px-4 py-2.5 text-sm font-semibold text-danger transition hover:bg-danger/20 disabled:opacity-60 sm:w-auto"
                >
                  Revoke Access Now
                </button>
              </div>
            )}
          </div>
        </>
      ) : null}

      {tokenOutcome?.type === 'approved' ? (
        <div
          className="mb-8 rounded-xl border border-accent/40 bg-accent/10 px-4 py-4 text-sm text-foreground"
          role="status"
        >
          <p className="font-semibold text-accent">
            Access approved. The support agent has been notified. This access expires at{' '}
            {formatDateTime(tokenOutcome.expiresAt)}.
          </p>
        </div>
      ) : null}

      {tokenOutcome?.type === 'denied' ? (
        <div className="mb-8 rounded-xl border border-border bg-surface/80 px-4 py-4 text-sm text-foreground" role="status">
          <p>Access denied. The support team has been notified.</p>
        </div>
      ) : null}

      {historyRevokeSuccess ? (
        <div
          className="mb-6 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm font-medium text-accent"
          role="status"
        >
          Admin support access has been revoked.
        </div>
      ) : null}

      <section className={`${cardClass} mt-8`}>
        <h2 className="text-lg font-semibold text-foreground">Support access history</h2>
        <p className="mt-1 text-sm text-muted">
          Every time logNsec requests temporary access, it appears here. You can revoke an active session at any time.
        </p>

        {historyState.kind === 'idle' || historyState.kind === 'loading' ? (
          <div className="mt-8 flex items-center justify-center gap-3 py-6">
            <Spinner className="h-6 w-6 text-accent" />
            <span className="text-sm text-muted">Loading history…</span>
          </div>
        ) : null}

        {historyState.kind === 'error' ? (
          <p className="mt-6 text-sm text-danger" role="alert">
            {historyState.message}
          </p>
        ) : null}

        {historyState.kind === 'empty' ? (
          <p className="mt-6 text-sm text-muted">No support access requests yet.</p>
        ) : null}

        {historyState.kind === 'ready' ? (
          <div className="mt-6 space-y-4">
            {activeRequestFromHistory ? (
              <div className="rounded-xl border border-danger/45 bg-danger/10 px-4 py-4">
                <p className="text-sm font-semibold text-danger">Active support session</p>
                <p className="mt-1 text-sm text-muted">
                  A logNsec agent currently has read access until {formatDateTime(activeRequestFromHistory.expires_at)}.
                </p>
                <button
                  type="button"
                  disabled={revokeBusy}
                  onClick={() => setRevokeModalRequestId(activeRequestFromHistory.id)}
                  className="mt-4 w-full rounded-lg border-2 border-danger px-4 py-2.5 text-sm font-semibold text-danger transition hover:bg-danger/10 disabled:opacity-60 sm:w-auto"
                >
                  Revoke Access Now
                </button>
              </div>
            ) : null}

            <ul className="relative space-y-0 border-l border-border pl-6">
              {historyState.rows.map((row) => (
                <li key={row.id} className="relative pb-8 last:pb-0">
                  <span
                    className="absolute -left-[25px] top-1.5 h-3 w-3 rounded-full bg-border ring-4 ring-surface"
                    aria-hidden
                  />
                  <div className="rounded-xl border border-border bg-navy/40 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClasses(row.status)}`}
                      >
                        {statusLabel(row.status)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-medium text-foreground">
                      {row.reason.trim() || 'No reason provided'}
                    </p>
                    <dl className="mt-3 grid gap-2 text-xs text-muted sm:grid-cols-2">
                      <div>
                        <dt className="text-muted">Requested</dt>
                        <dd className="text-foreground">{formatDateTime(row.requested_at)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted">Expires</dt>
                        <dd className="text-foreground">{formatDateTime(row.expires_at)}</dd>
                      </div>
                    </dl>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <p className="mt-8 text-center text-sm text-muted">
        <Link to="/settings" className="font-medium text-accent hover:underline">
          Back to settings
        </Link>
      </p>

      {revokeModalRequestId ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="revoke-support-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-xl">
            <h2 id="revoke-support-title" className="text-lg font-semibold text-foreground">
              Revoke access?
            </h2>
            <p className="mt-3 text-sm text-muted">
              Are you sure you want to revoke admin access to your account?
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setRevokeModalRequestId(null)}
                className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground/5"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={revokeBusy}
                onClick={() => void confirmRevoke(revokeModalRequestId)}
                className="rounded-lg bg-danger px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-danger/90 disabled:opacity-60"
              >
                {revokeBusy ? 'Revoking…' : 'Revoke access'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
